import React, { useState } from "react";
import { Target, Plus, Edit, Trash2, CheckCircle, Clock } from "lucide-react";

/**
 * Goals Page Component
 * Manage and track marketing/business goals
 */
const Goals = () => {
  const [goals, setGoals] = useState([
    {
      id: 1,
      name: "Q1 Revenue Target",
      description: "Achieve $60,000 in total revenue for Q1 2024",
      target: 60000,
      current: 45680,
      targetDate: "2024-03-31",
      type: "revenue",
      isAchieved: false,
    },
    {
      id: 2,
      name: "Lead Generation",
      description: "Generate 400 qualified leads this quarter",
      target: 400,
      current: 342,
      targetDate: "2024-03-31",
      type: "leads",
      isAchieved: false,
    },
    {
      id: 3,
      name: "Cost Per Lead",
      description: "Reduce average cost per lead to under $20",
      target: 20,
      current: 24.62,
      targetDate: "2024-02-29",
      type: "cost_per_lead",
      isAchieved: false,
    },
  ]);

  const [showAddForm, setShowAddForm] = useState(false);

  const getProgress = (goal) => {
    if (goal.type === "cost_per_lead") {
      // For cost metrics, progress is based on how close to target (lower is better)
      return Math.max(0, Math.min(100, (goal.target / goal.current) * 100));
    }
    return Math.min(100, (goal.current / goal.target) * 100);
  };

  const formatValue = (value, type) => {
    switch (type) {
      case "revenue":
        return `$${value.toLocaleString()}`;
      case "cost_per_lead":
        return `$${value}`;
      default:
        return value.toLocaleString();
    }
  };

  const getStatusColor = (goal) => {
    const progress = getProgress(goal);
    if (goal.isAchieved) return "text-green-600";
    if (progress >= 80) return "text-blue-600";
    if (progress >= 60) return "text-yellow-600";
    return "text-gray-600";
  };

  const getStatusIcon = (goal) => {
    if (goal.isAchieved) return CheckCircle;
    return Clock;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Goals</h1>
          <p className="text-sm md:text-base text-gray-600">
            Track your marketing and business objectives
          </p>
        </div>

        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary flex items-center justify-center space-x-2 touch-manipulation min-h-[44px] w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Add Goal</span>
        </button>
      </div>

      {/* Goals Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {goals.map((goal) => {
          const progress = getProgress(goal);
          const StatusIcon = getStatusIcon(goal);
          const daysLeft = Math.ceil(
            (new Date(goal.targetDate) - new Date()) / (1000 * 60 * 60 * 24),
          );

          return (
            <div key={goal.id} className="card p-4 md:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-2 md:space-x-3 flex-1 min-w-0">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-semibold text-gray-900">
                      {goal.name}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-500 line-clamp-2">
                      {goal.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                  <button
                    className="p-2 hover:bg-gray-100 rounded touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label="Edit goal"
                  >
                    <Edit className="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    className="p-2 hover:bg-gray-100 rounded touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label="Delete goal"
                  >
                    <Trash2 className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 md:space-y-4">
                {/* Progress */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs md:text-sm font-medium text-gray-700">
                      Progress
                    </span>
                    <span
                      className={`text-xs md:text-sm font-medium ${getStatusColor(goal)}`}
                    >
                      {progress.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Current vs Target */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Current</p>
                    <p className="text-base md:text-lg font-semibold text-gray-900 truncate">
                      {formatValue(goal.current, goal.type)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Target</p>
                    <p className="text-base md:text-lg font-semibold text-gray-900 truncate">
                      {formatValue(goal.target, goal.type)}
                    </p>
                  </div>
                </div>

                {/* Status and Days Left */}
                <div className="flex items-center justify-between pt-3 md:pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <StatusIcon
                      className={`w-3 h-3 md:w-4 md:h-4 ${getStatusColor(goal)}`}
                    />
                    <span
                      className={`text-xs md:text-sm ${getStatusColor(goal)}`}
                    >
                      {goal.isAchieved ? "Achieved" : "In Progress"}
                    </span>
                  </div>

                  <div className="text-xs md:text-sm text-gray-500">
                    {daysLeft > 0
                      ? `${daysLeft} days left`
                      : daysLeft === 0
                        ? "Due today"
                        : `${Math.abs(daysLeft)} days overdue`}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Goal Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
              Add New Goal
            </h2>

            <form className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Goal Name
                </label>
                <input
                  type="text"
                  className="input-field text-sm md:text-base"
                  placeholder="e.g., Increase Monthly Revenue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="input-field text-sm md:text-base"
                  rows="3"
                  placeholder="Describe your goal in detail..."
                ></textarea>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Value
                  </label>
                  <input
                    type="number"
                    className="input-field text-sm md:text-base"
                    placeholder="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Goal Type
                  </label>
                  <select className="input-field text-sm md:text-base">
                    <option value="revenue">Revenue</option>
                    <option value="leads">Leads</option>
                    <option value="cost_per_lead">Cost per Lead</option>
                    <option value="conversion_rate">Conversion Rate</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Date
                </label>
                <input
                  type="date"
                  className="input-field text-sm md:text-base"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn-secondary touch-manipulation min-h-[44px] w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary touch-manipulation min-h-[44px] w-full sm:w-auto"
                >
                  Create Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goals;
